import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Trophy, Star, Clock, MousePointer2, Sparkles, PartyPopper, Save, X, Loader2, ChevronLeft, Swords, Share2, Facebook, Instagram, MessageCircle, Copy, Check, Download, RotateCcw, Shuffle, ArrowLeft } from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';
import { getGameShareUrl, getProductionUrl, getGameShareImageUrl, handleInstagramShare, handleMessengerShare, handleFacebookShare } from './challengeUtils';
import { ChallengeShareActions } from './ChallengeShareActions';
import { toast } from 'sonner';
import { isProfane, sanitizeName } from '../../lib/profanityFilter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get best score key for localStorage
const getBestScoreKey = (imageUrl, difficulty) => {
  // Create a hash-like key from image URL and difficulty
  const urlHash = imageUrl ? btoa(imageUrl).slice(0, 20) : 'unknown';
  return `jigsaw_best_score_${urlHash}_${difficulty}`;
};

// Get best score from localStorage
const getBestScore = (imageUrl, difficulty) => {
  try {
    const key = getBestScoreKey(imageUrl, difficulty);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Save best score to localStorage
const saveBestScore = (imageUrl, difficulty, scoreData) => {
  try {
    const key = getBestScoreKey(imageUrl, difficulty);
    localStorage.setItem(key, JSON.stringify(scoreData));
  } catch (e) {
    logger.error('Failed to save best score:', e);
  }
};

// Confetti particle component
function Confetti() {
  const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    color: colors[Math.floor(Math.random() * colors.length)],
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    size: 6 + Math.random() * 8,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-confetti"
          style={{
            left: `${p.left}%`,
            top: '-20px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

export function CompletionModal({ 
  isOpen, 
  onClose, 
  scoreData, 
  time, 
  moves, 
  difficulty,
  imageUrl,
  deviceId,
  onNewPuzzle,
  onReplayPuzzle,
  onChallengeFriend,
  gridSize = 6,
  hintPenalty = 0,
  isFirstPlay = true  // First Score Feature: whether this is the first play of this image
}) {
  const [step, setStep] = useState('congratulations'); // 'congratulations' | 'enterName' | 'share'
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [copied, setCopied] = useState(false);
  const [savedScoreId, setSavedScoreId] = useState(null);
  const [previousBestScore, setPreviousBestScore] = useState(null);
  const [isNewBestScore, setIsNewBestScore] = useState(false);
  const [leaderboardSaveAttempted, setLeaderboardSaveAttempted] = useState(false);
  const [leaderboardSaveResult, setLeaderboardSaveResult] = useState(null);
  // Synchronous double-submit guard. setIsSubmitting is async (state batches),
  // so a quick double-tap on Save Score can fire two POSTs before React
  // re-renders the disabled button. A ref flips immediately and blocks
  // the second call — backstops the DB unique partial index on
  // (image_url, difficulty, device_id).
  const submitLockRef = useRef(false);
  
  // Reset state when modal opens and check for best score
  useEffect(() => {
    if (isOpen && scoreData) {
      setStep('congratulations');
      setPlayerName(sanitizeName(localStorage.getItem('jigsaw_player_name') || ''));
      setIsSubmitting(false);
      setShowConfetti(true);
      setCopied(false);
      setSavedScoreId(null);
      setLeaderboardSaveAttempted(false);
      setLeaderboardSaveResult(null);
      submitLockRef.current = false;
      
      // Check if this is a new best score
      const existingBest = getBestScore(imageUrl, difficulty);
      setPreviousBestScore(existingBest);
      
      // Apply hint penalty to get final score for comparison
      const currentFinalScore = Math.max(0, scoreData.score - hintPenalty);
      if (!existingBest || currentFinalScore > existingBest.score) {
        setIsNewBestScore(true);
        // Save new best score locally
        saveBestScore(imageUrl, difficulty, {
          score: currentFinalScore,
          time,
          moves,
          isPerfect: scoreData.isPerfect,
          hintPenalty,
          date: new Date().toISOString()
        });
      } else {
        setIsNewBestScore(false);
      }
      
      // Stop confetti after 4 seconds
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scoreData, imageUrl, difficulty, time, moves, hintPenalty]);
  
  if (!scoreData) return null;
  
  const { score: baseScore, isPerfect, breakdown } = scoreData;
  // Apply hint penalty to final score
  const finalScore = Math.max(0, baseScore - hintPenalty);
  const diffConfig = DIFFICULTIES[difficulty];
  
  const handleSaveClick = async () => {
    // If name is already entered, submit directly (skip enterName step)
    if (playerName.trim()) {
      await handleDirectSave();
    } else {
      setStep('enterName');
    }
  };
  
  // Direct save without going to enterName step
  const handleDirectSave = async () => {
    if (!playerName.trim() || isSubmitting) return;
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    if (isProfane(playerName.trim())) {
      toast.error('Profanity Detected! - Try Again');
      submitLockRef.current = false;
      return;
    }

    setIsSubmitting(true);
    setLeaderboardSaveAttempted(true);
    
    try {
      // Save to leaderboard with grid_size, is_mobile, and device_id parameters
      // Backend will check if this is first play and reject if it's a replay
      const response = await axios.post(
        `${API}/leaderboard?is_mobile=${isMobileDevice()}&device_id=${deviceId || ''}`, 
        {
          player_name: playerName.trim(),
          time_seconds: time,
          moves,
          image_url: imageUrl,
          difficulty,
          grid_size: gridSize
        }
      );
      
      // Check if this was a first play (score saved to leaderboard)
      const wasFirstPlay = response.data?.is_first_play !== false;
      setLeaderboardSaveResult(wasFirstPlay ? 'saved' : 'replay');
      
      // Save the score ID for sharing (only if actually saved to leaderboard)
      if (response.data && response.data.id) {
        setSavedScoreId(response.data.id);
      }
      
      // For replays, still save to completed puzzles history for the History feature
      // (First plays are already saved by the leaderboard endpoint)
      if (deviceId && !wasFirstPlay) {
        try {
          await axios.post(`${API}/images/completed`, {
            device_id: deviceId,
            image_url: imageUrl,
            player_name: playerName.trim(),
            time_seconds: time,
            moves,
            score: finalScore,
            difficulty,
            perfect_game: scoreData.isPerfect || false,
            grid_size: gridSize,
            hints_used: hintPenalty > 0 ? Math.round(hintPenalty / 25) : 0
          });
        } catch (historyError) {
          logger.error('Failed to save to history:', historyError);
        }
      }
      
      // Save player name to localStorage for future use
      localStorage.setItem('jigsaw_player_name', playerName.trim());
      
      // Success - go to share step
      setStep('share');
      setIsSubmitting(false);
    } catch (error) {
      logger.error('Failed to save score:', error);
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };
  
  const handleBackClick = () => {
    setStep('congratulations');
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playerName.trim() || isSubmitting) return;
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    
    setIsSubmitting(true);
    setLeaderboardSaveAttempted(true);
    
    try {
      // Save to leaderboard with grid_size, is_mobile, and device_id parameters
      // Backend will check if this is first play and reject if it's a replay
      const response = await axios.post(
        `${API}/leaderboard?is_mobile=${isMobileDevice()}&device_id=${deviceId || ''}`,
        {
          player_name: playerName.trim(),
          time_seconds: time,
          moves,
          image_url: imageUrl,
          difficulty,
          grid_size: gridSize
        }
      );
      
      // Check if this was a first play (score saved to leaderboard)
      const wasFirstPlay = response.data?.is_first_play !== false;
      setLeaderboardSaveResult(wasFirstPlay ? 'saved' : 'replay');
      
      // Save the score ID for sharing (only if actually saved to leaderboard)
      if (response.data && response.data.id) {
        setSavedScoreId(response.data.id);
      }
      
      // For replays, still save to completed puzzles history for the History feature
      // (First plays are already saved by the leaderboard endpoint)
      if (deviceId && !wasFirstPlay) {
        try {
          await axios.post(`${API}/images/completed`, {
            device_id: deviceId,
            image_url: imageUrl,
            player_name: playerName.trim(),
            time_seconds: time,
            moves,
            score: finalScore,
            difficulty,
            perfect_game: scoreData.isPerfect || false,
            grid_size: gridSize,
            hints_used: hintPenalty > 0 ? Math.round(hintPenalty / 25) : 0
          });
        } catch (historyError) {
          logger.error('Failed to save to history:', historyError);
          // Don't block the flow if history save fails
        }
      }
      
      // Save player name to localStorage for future use
      localStorage.setItem('jigsaw_player_name', playerName.trim());
      
      // Success - go to share step
      setStep('share');
      setIsSubmitting(false);
    } catch (error) {
      logger.error('Failed to submit score:', error);
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };
  
  const handleShare = (platform) => {
    const scoreText = `I scored ${Math.round(finalScore)} points on JigSaw Blocks${isPerfect ? ' with a perfect game!' : '!'} Can you beat me?`;
    const productionUrl = getProductionUrl();
    
    if (platform === 'messenger') {
      // Use share page URL with OG tags so scorecard appears as preview
      if (savedScoreId) {
        handleMessengerShare(getGameShareUrl(savedScoreId));
      } else {
        handleMessengerShare(productionUrl);
      }
    } else if (platform === 'instagram') {
      // Download image and copy text for Instagram
      if (savedScoreId) {
        const imageUrl = getGameShareImageUrl(savedScoreId);
        handleInstagramShare(
          imageUrl,
          `${scoreText}\n${productionUrl}`,
          () => {
            toast.success('Copied! Open Instagram and paste in your story or post');
          },
          () => {
            toast.error('Failed to download image');
          }
        );
      } else {
        // Fallback - just copy text
        navigator.clipboard.writeText(`${scoreText}\n${productionUrl}`);
        toast.success('Copied! Open Instagram and paste in your story or post');
      }
    } else if (platform === 'facebook') {
      // Use production share page URL for Facebook - it has OG meta tags with score card image
      if (savedScoreId) {
        const sharePageUrl = getGameShareUrl(savedScoreId);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      } else {
        // Fallback to simple URL if no score ID
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productionUrl)}&quote=${encodeURIComponent(scoreText)}`,
          '_blank',
          'width=600,height=400'
        );
      }
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(`${scoreText}\n${productionUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleCancel = () => {
    onNewPuzzle();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-auto p-0 border-2 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto box-border" hideCloseButton>
        <DialogTitle className="sr-only">Puzzle Complete</DialogTitle>
        {/* Confetti Effect */}
        {showConfetti && <Confetti />}
        
        {step === 'congratulations' && (
          /* Step 1: Congratulations Screen */
          <div className="relative overflow-hidden w-full max-w-full">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onNewPuzzle}
              className="absolute top-2 right-2 h-8 w-8 z-20 bg-background/50 hover:bg-background/80"
              data-testid="completion-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {/* Gradient Header - more compact on mobile */}
            <div className="bg-gradient-to-br from-primary/20 via-accent/20 to-primary/10 pt-4 pb-3 px-3 sm:pt-8 sm:pb-6 sm:px-6 overflow-hidden">
              {/* Animated Trophy - smaller on mobile, contained animations */}
              <div className="flex justify-center mb-2 sm:mb-4">
                <div className="relative w-12 h-12 sm:w-20 sm:h-20 overflow-hidden">
                  <div className="absolute inset-0 bg-primary/30 rounded-full blur-2xl animate-pulse" />
                  <div className="absolute inset-1 bg-accent/20 rounded-full blur-xl animate-ping" style={{ animationDuration: '2s' }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-gradient-to-br from-primary to-accent p-3 sm:p-5 rounded-full shadow-lg">
                      <PartyPopper className="h-6 w-6 sm:h-10 sm:w-10 text-primary-foreground animate-bounce" style={{ animationDuration: '1s' }} />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Title */}
              <h2 className="text-xl sm:text-3xl font-heading font-bold text-center text-foreground">
                🎉 Congratulations! 🎉
              </h2>
              <p className="text-center text-muted-foreground mt-1 text-xs sm:text-base">
                {isPerfect ? "Perfect game! You're a puzzle master!" : "You solved the puzzle!"}
              </p>
            </div>
            
            {/* Content - more compact spacing on mobile */}
            <div className="px-3 pb-4 pt-2 space-y-2 sm:px-6 sm:pb-6 sm:pt-4 sm:space-y-5">
              {/* Big Score Display */}
              <div className="text-center py-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20">
                  <Trophy className="h-5 w-5 sm:h-8 sm:w-8 text-primary" />
                  <div>
                    <div className="text-2xl sm:text-4xl font-heading font-bold text-gradient">
                      {Math.round(finalScore)}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Points</div>
                  </div>
                </div>
              </div>
              
              {/* Stats Row */}
              <div className="flex justify-center gap-1.5 sm:gap-6 flex-wrap">
                <div className="flex items-center gap-0.5 sm:gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3 sm:h-5 sm:w-5 flex-shrink-0" />
                  <span className="font-semibold text-foreground text-[11px] sm:text-base whitespace-nowrap">{formatTime(time)}</span>
                </div>
                <div className="flex items-center gap-0.5 sm:gap-1 text-muted-foreground">
                  <MousePointer2 className="h-3 w-3 sm:h-5 sm:w-5 flex-shrink-0" />
                  <span className="font-semibold text-foreground text-[11px] sm:text-base whitespace-nowrap">{moves} moves</span>
                </div>
                <div className={cn(
                  "flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap",
                  difficulty === 'easy' && "bg-success/10 text-success",
                  difficulty === 'normal' && "bg-primary/10 text-primary",
                  difficulty === 'hard' && "bg-destructive/10 text-destructive"
                )}>
                  {diffConfig.name} ×{diffConfig.multiplier}
                </div>
              </div>
              
              {/* Perfect Game Badge - compact on mobile */}
              {isPerfect && (
                <div className="flex items-center justify-center gap-1 py-1.5 sm:py-3 px-2 sm:px-4 rounded-lg sm:rounded-xl bg-gradient-to-r from-warning/20 to-warning/10 border border-warning/30">
                  <Star className="h-3 w-3 sm:h-5 sm:w-5 text-warning fill-warning animate-pulse" />
                  <span className="font-semibold text-warning text-xs sm:text-base">Perfect! +{breakdown.perfectBonus}</span>
                  <Star className="h-3 w-3 sm:h-5 sm:w-5 text-warning fill-warning animate-pulse" />
                </div>
              )}
              
              {/* New Best Score / Previous Best Score Info - compact */}
              {isNewBestScore ? (
                <div className="flex items-center justify-center gap-1 py-1.5 px-2 sm:px-4 rounded-lg bg-success/10 border border-success/30">
                  <Trophy className="h-3 w-3 sm:h-4 sm:w-4 text-success" />
                  <span className="text-xs font-medium text-success">
                    New Best!
                    {previousBestScore && (
                      <span className="text-muted-foreground ml-1">
                        (was {Math.round(previousBestScore.score)})
                      </span>
                    )}
                  </span>
                </div>
              ) : previousBestScore && (
                <div className="flex items-center justify-center gap-1 py-1.5 px-2 sm:px-4 rounded-lg bg-muted/50 border border-border">
                  <span className="text-xs text-muted-foreground">
                    Best: <span className="font-semibold text-foreground">{Math.round(previousBestScore.score)}</span>
                  </span>
                </div>
              )}
              
              {/* Score Breakdown - hidden on very small mobile, compact otherwise */}
              <div className="bg-muted/30 rounded-lg sm:rounded-xl p-2 sm:p-4 space-y-1 sm:space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:block mb-2">Score Breakdown</div>
                <div className={cn(
                  "grid gap-0.5 sm:gap-4 text-[10px] sm:text-sm",
                  hintPenalty > 0 ? "grid-cols-4" : "grid-cols-3"
                )}>
                  <div className="text-center min-w-0">
                    <div className="font-semibold text-foreground truncate">{breakdown.timeScore}</div>
                    <div className="text-[9px] sm:text-xs text-muted-foreground">Time</div>
                  </div>
                  <div className="text-center min-w-0">
                    <div className="font-semibold text-foreground truncate">{breakdown.moveScore}</div>
                    <div className="text-[9px] sm:text-xs text-muted-foreground">Moves</div>
                  </div>
                  <div className="text-center min-w-0">
                    <div className="font-semibold text-foreground truncate">{breakdown.completionBonus}</div>
                    <div className="text-[9px] sm:text-xs text-muted-foreground">Bonus</div>
                  </div>
                  {hintPenalty > 0 && (
                    <div className="text-center min-w-0">
                      <div className="font-semibold text-destructive truncate">-{hintPenalty}</div>
                      <div className="text-[9px] sm:text-xs text-muted-foreground">Hints</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Button - New Puzzle */}
              <div className="flex gap-1.5 sm:gap-2 pt-1">
                <Button 
                  variant="outline"
                  className="flex-1 h-9 sm:h-12 text-[11px] sm:text-base px-2 sm:px-4 min-w-0"
                  onClick={onNewPuzzle}
                  data-testid="new-puzzle-btn"
                >
                  <Shuffle className="h-3 w-3 sm:h-5 sm:w-5 mr-0.5 sm:mr-1 flex-shrink-0" />
                  <span className="truncate">New Puzzle</span>
                </Button>
              </div>
              
              {/* Name Input directly above Save Score */}
              <div className="pt-1">
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(sanitizeName(e.target.value))}
                  className="text-center h-10"
                  maxLength={20}
                  data-testid="player-name-input"
                />
              </div>
              
              {/* First Score Feature: Show replay notice */}
              {!isFirstPlay && (
                <div className="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <RotateCcw className="h-3 w-3 text-amber-600" />
                  <span className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-500">
                    Replay: Score saves as personal best only, not to leaderboard
                  </span>
                </div>
              )}
              
              {/* Save Score Button - always blue gradient */}
              <Button 
                className="w-full h-9 sm:h-12 text-xs sm:text-base bg-gradient-to-r from-primary to-accent hover:opacity-90"
                onClick={handleSaveClick}
                disabled={isSubmitting}
                data-testid="save-score-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="h-3 w-3 sm:h-5 sm:w-5 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3 sm:h-5 sm:w-5 mr-1" />
                )}
                {isFirstPlay ? 'Save Score' : 'Save Personal Best'}
              </Button>
            </div>
          </div>
        )}
        
        {step === 'enterName' && (
          /* Step 2: Enter Name Screen */
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 relative">
            {/* Back Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute top-3 left-3 sm:top-4 sm:left-4"
              onClick={handleBackClick}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleCancel}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 h-8 w-8"
              data-testid="completion-entername-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            
            {/* Header */}
            <div className="text-center pt-4 sm:pt-6">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-3 sm:mb-4">
                <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">Save Your Score</h2>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                Enter your name for the leaderboard
              </p>
            </div>
            
            {/* Score Reminder */}
            <div className="text-center">
              <span className="text-2xl sm:text-3xl font-heading font-bold text-gradient">{Math.round(finalScore)}</span>
              <span className="text-muted-foreground ml-2 text-sm sm:text-base">points</span>
            </div>
            
            {/* Name Form */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <Input
                  id="playerName"
                  placeholder="Enter your name..."
                  value={playerName}
                  onChange={(e) => setPlayerName(sanitizeName(e.target.value))}
                  maxLength={20}
                  className="h-10 sm:h-12 text-center text-base sm:text-lg"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-center">
                  Max 20 characters
                </p>
              </div>
              
              <div className="flex gap-2 sm:gap-3">
                <Button 
                  type="button"
                  variant="outline" 
                  className="flex-1 h-10 sm:h-12 text-sm sm:text-base"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 h-10 sm:h-12 text-sm sm:text-base bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={!playerName.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                      Save Score
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}
        
        {step === 'share' && (
          /* Step 3: Share Screen */
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 relative">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onNewPuzzle}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 h-8 w-8"
              data-testid="completion-share-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {/* Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-success/10 mb-3 sm:mb-4">
                <Check className="h-6 w-6 sm:h-8 sm:w-8 text-success" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">
                {leaderboardSaveResult === 'replay' ? 'Personal Best Saved!' : 'Score Saved!'}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base whitespace-pre-line">
                {leaderboardSaveResult === 'replay' 
                  ? 'Replay scores are saved as personal bests only'
                  : 'Share your achievement!\nand Challenge your Friends'
                }
              </p>
            </div>
            
            {/* First Score Feature: Show notice if replay */}
            {leaderboardSaveResult === 'replay' && (
              <div className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <RotateCcw className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-xs text-amber-700 dark:text-amber-500 text-center">
                  Only your first attempt on each puzzle counts for the global leaderboard
                </span>
              </div>
            )}
            
            {/* Score Display */}
            <div className="text-center py-3 sm:py-4 bg-muted/30 rounded-xl">
              <div className="text-3xl sm:text-4xl font-heading font-bold text-gradient">{Math.round(finalScore)}</div>
              <div className="text-sm text-muted-foreground">points</div>
            </div>
            
            {/* Share Actions: View / Facebook / Other Social Media */}
            <ChallengeShareActions
              imageUrl={imageUrl}
              difficulty={difficulty}
              scoreData={scoreData}
              time={time}
              moves={moves}
              deviceId={deviceId}
              playerName={playerName}
            />
            
            {/* New Puzzle Button */}
            <div className="flex gap-2 sm:gap-3">
              <Button 
                className="flex-1 h-10 sm:h-12 text-sm sm:text-base bg-gradient-to-r from-primary to-accent hover:opacity-90"
                onClick={onNewPuzzle}
                data-testid="share-new-puzzle-btn"
              >
                <Shuffle className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                New Puzzle
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
